import type { ReactNode } from 'react';
import type { MemberPermissions } from '../../types/domain';
import {
	Inbox as InboxIcon,
	DashboardOutlined as DashboardIcon,
	ListAltOutlined as ApplicationIcon,
	VideoCameraFrontOutlined as InterviewIcon,
	Groups2Outlined as MembersIcon,
	StackedBarChartOutlined as FinancesIcon,
	FiberNewOutlined as NewIcon,
	KeyboardReturnOutlined as ReturningIcon,
	SchoolOutlined as SchoolIcon,
	Inventory2Outlined as InventoryIcon,
	ArchiveOutlined as ArchiveIcon,
	SettingsOutlined as SettingsIcon,
	AssignmentLateOutlined as IncompleteStatusIcon,
	AssignmentTurnedInOutlined as GoodStatusIcon,
	AssignmentReturnOutlined as RejectedStatusIcon,
	AssignmentOutlined as NeutralStatusIcon,
	ForwardToInboxOutlined as ContactCenterIcon,
	MailOutlined as RequestIcon,
	EventAvailable as EventIcon,
	ForumOutlined as ChatIcon,
} from '@mui/icons-material';
import { useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { generatePath } from './routeUtils';
import { paths } from './paths';

interface SidebarPage {
	icon: ReactNode;
	link: string;
	text: string;
	requiredPermissions: string[];
}

interface SidebarSection {
	title: string;
	pages: SidebarPage[];
}

interface ProcessedPage {
	icon: ReactNode;
	link: string;
	text: string;
	disable: boolean | undefined;
}

interface ProcessedSection {
	title: string;
	pages: ProcessedPage[];
}

const currYear: number = new Date().getFullYear();

const sidebarTemplate: SidebarSection[] = [
	{ title: 'MAIN', pages: [{ icon: <DashboardIcon className='icon' />, link: generatePath(paths.memberDash), text: 'Dashboard', requiredPermissions: ['login'] }] },
	{
		title: 'ADMIN',
		pages: [
			{ icon: <InboxIcon className='icon' />, link: generatePath(paths.inbox), text: 'Inbox', requiredPermissions: ['email'] },
			{ icon: <ChatIcon className='icon' />, link: generatePath(paths.chatInbox), text: 'Chat', requiredPermissions: ['chat'] },
			{ icon: <ApplicationIcon className='icon' />, link: generatePath(paths.allAppsInYear, { year: currYear }), text: 'Applications', requiredPermissions: ['admin', 'applications'] },
			{ icon: <RequestIcon className='icon' />, link: generatePath(paths.allRequests), text: 'Requests', requiredPermissions: ['admin', 'applications'] },
			{ icon: <InterviewIcon className='icon' />, link: generatePath(paths.interviewDash), text: 'Interviews', requiredPermissions: ['admin', 'interviews.canAccess'] },
			{ icon: <EventIcon className='icon' />, link: generatePath(paths.scheduling), text: 'Scheduling', requiredPermissions: ['admin', 'interviews.canSchedule'] },
			{ icon: <MembersIcon className='icon' />, link: generatePath(paths.allMembers), text: 'Members', requiredPermissions: ['admin', 'members'] },
			{ icon: <MembersIcon className='icon' />, link: generatePath(paths.allApplicants), text: 'Applicants', requiredPermissions: ['admin', 'applications'] },
			{ icon: <SettingsIcon className='icon' />, link: generatePath(paths.siteSettings), text: 'Settings', requiredPermissions: ['admin', 'site'] },
			{ icon: <FinancesIcon className='icon' />, link: generatePath(paths.cycleExpenses), text: 'Finances', requiredPermissions: ['finances'] },
			{ icon: <ContactCenterIcon className='icon' />, link: generatePath(paths.contactCenter), text: 'Sender', requiredPermissions: ['push'] },
		],
	},
	{
		title: 'CURRENT',
		pages: [
			{ icon: <NewIcon className='icon' />, link: generatePath(paths.newAppsInYear, { year: currYear }), text: 'New Apps', requiredPermissions: ['admin', 'applications'] },
			{ icon: <ReturningIcon className='icon' />, link: generatePath(paths.returningAppsInYear, { year: currYear }), text: 'Grants', requiredPermissions: ['admin', 'applications'] },
			{ icon: <SchoolIcon className='icon' />, link: generatePath(paths.scholarshipAppsInYear, { year: currYear }), text: 'Scholarships', requiredPermissions: ['admin', 'applications'] },
		],
	},
	{
		title: 'STATUS',
		pages: [
			{ icon: <IncompleteStatusIcon className='icon' />, link: generatePath(paths.incompleteApps), text: 'Incomplete', requiredPermissions: ['admin', 'applications'] },
			{ icon: <GoodStatusIcon className='icon' />, link: generatePath(paths.completedApps), text: 'Completed', requiredPermissions: ['admin', 'applications'] },
			{ icon: <GoodStatusIcon className='icon' />, link: generatePath(paths.eligibleApps), text: 'Eligible', requiredPermissions: ['admin', 'applications'] },
			{ icon: <GoodStatusIcon className='icon' />, link: generatePath(paths.invitedApps), text: 'Invited', requiredPermissions: ['admin', 'applications'] },
			{ icon: <GoodStatusIcon className='icon' />, link: generatePath(paths.awardedApps), text: 'Awarded', requiredPermissions: ['admin', 'applications'] },
			{ icon: <RejectedStatusIcon className='icon' />, link: generatePath(paths.rejectedApps), text: 'Rejected', requiredPermissions: ['admin', 'applications'] },
			{ icon: <NeutralStatusIcon className='icon' />, link: generatePath(paths.deletedApps), text: 'Deleted', requiredPermissions: ['admin', 'applications'] },
		],
	},
	{
		title: 'PAST',
		pages: [
			{ icon: <ArchiveIcon className='icon' />, link: generatePath(paths.allAppsInYear, { year: currYear - 1 }), text: `${currYear - 1}`, requiredPermissions: ['admin', 'applications'] },
			{ icon: <ArchiveIcon className='icon' />, link: generatePath(paths.allAppsInYear, { year: currYear - 2 }), text: `${currYear - 2}`, requiredPermissions: ['admin', 'applications'] },
			{ icon: <ArchiveIcon className='icon' />, link: generatePath(paths.allAppsInYear, { year: currYear - 3 }), text: `${currYear - 3}`, requiredPermissions: ['admin', 'applications'] },
			{ icon: <InventoryIcon className='icon' />, link: generatePath(paths.archives), text: 'Archives', requiredPermissions: ['admin', 'archives'] },
		],
	},
];

const hasPermission = (permissions: MemberPermissions, permPath: string): unknown => {
	if (!permissions) return false;
	return permPath.split('.').reduce<unknown>((acc, part) => (acc ? (acc as Record<string, unknown>)[part] : undefined), permissions);
};

const processPagePermissions = (page: SidebarPage, permissions: MemberPermissions): ProcessedPage => {
	const { requiredPermissions, ...restOfPage } = page;
	let isDisabled: boolean | undefined;

	if (requiredPermissions) {
		const hasAdminPerm = hasPermission(permissions, 'admin');

		if (hasAdminPerm) {
            isDisabled = false;
		} else {
			const hasRequiredPerms = requiredPermissions.some((p) => hasPermission(permissions, p));
			isDisabled = !hasRequiredPerms;
		}
	}

	return { ...restOfPage, disable: isDisabled };
};

export const useSidebarMenu = (): ProcessedSection[] => {
	const { member } = useAuth();

	return useMemo((): ProcessedSection[] => {
		const permissions: MemberPermissions = member?.permissions || {};
		return sidebarTemplate.map((section) => ({
			...section,
			pages: section.pages.map((page) => processPagePermissions(page, permissions)),
		}));
	}, [member]);
};
